/**
 * "Что произойдёт" block (KAN-595): tells the operator, before the list is
 * created/uploaded, what the chosen kind will do. Data: outcome-preview API;
 * formatting: lib/outcomePreview.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { datasetsApi } from '@/services/api/datasets';
import { describeOutcome } from '@/lib/outcomePreview';
import type { CampaignKind } from '@/types/dataset';

interface OutcomePreviewProps {
  kind: CampaignKind;
  /** KAN-752: campaign operator of a Telegram list (several campaigns in the cabinet). */
  ownerId?: string;
}

export function OutcomePreview({ kind, ownerId }: OutcomePreviewProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ownerId ? ['outcomePreview', kind, ownerId] : ['outcomePreview', kind],
    queryFn: () =>
      ownerId ? datasetsApi.outcomePreview(kind, ownerId) : datasetsApi.outcomePreview(kind),
  });

  if (isLoading) {
    return (
      <div
        data-testid="outcome-preview-loading"
        className="flex items-center gap-2 text-sm text-gray-500"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Загрузка настроек канала…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div
        data-testid="outcome-preview-error"
        role="alert"
        className="text-sm text-amber-700"
      >
        Не удалось загрузить описание того, что произойдёт. Список можно
        создать, но настройки канала проверьте в кабинете.
      </div>
    );
  }

  const view = describeOutcome(data);
  const isEmpty =
    view.facts.length === 0 && view.warnings.length === 0 && !view.error;

  return (
    <div
      data-testid="outcome-preview"
      data-kind={data.kind}
      className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"
    >
      <h4 className="font-medium">Что произойдёт</h4>

      {view.error && (
        <div
          data-testid="outcome-preview-activation-error"
          role="alert"
          className="flex items-start gap-2 text-red-600"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{view.error}</span>
        </div>
      )}

      {view.warnings.map((text) => (
        <div
          key={text}
          data-testid="outcome-preview-warning"
          className="flex items-start gap-2 text-amber-700"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{text}</span>
        </div>
      ))}

      {isEmpty ? (
        <p data-testid="outcome-preview-empty" className="text-gray-600">
          Настройки канала пока не заданы.
        </p>
      ) : (
        <ul className="list-disc space-y-0.5 pl-5">
          {view.facts.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      )}

      <ul className="list-disc space-y-0.5 pl-5 text-blue-800">
        {view.notes.map((text) => (
          <li key={text}>{text}</li>
        ))}
      </ul>
    </div>
  );
}
